import { useCallback, useMemo, useRef } from "react";

import { renderToStaticMarkup } from "react-dom/server";

import { ColumnChartGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { percentageSignFilter } from "app/filters/ngFilters";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context";
import { PdfExportService } from "app/services/PdfExportService";
import { useTranslation } from "pro-features/components/WithTranslation";

import { useDemographicsAgeDistributionComparisonData } from "../../hooks/useDemographicsAgeDistributionComparisonData";
import { processAgeDistributionComparisonData } from "../../utils/dataProcessors";

import { X_AXIS_AGE } from "./AgeDistributionBarChart";
import { AgeDistributionComparisonChartLegends } from "./AgeDistributionComparisonChartLegends";
import { useLegendItems } from "./useLegendItems";

const valueFormatter = (value: number) => percentageSignFilter()(value / 100, 0);

export const IndustryAnalysisDemographicsAgeDistributionChartComparison = () => {
    const t = useTranslation();
    const subtitleFilters = useSubtitleFilters("date");
    const { categories } = useCategoriesComparison();
    const chartRef = useRef(null);
    const { data, isLoading, isError } = useDemographicsAgeDistributionComparisonData(categories);
    const { legendItems, toggleItem } = useLegendItems(data, categories);

    const handleExportChartPng = useCallback(() => {
        if (chartRef.current) {
            const styleHTML = PdfExportService.getStylesheetsForPng();

            const offSetX = 0;
            const offSetY = 50;
            return PdfExportService.downloadHtmlPngFedService(
                styleHTML + chartRef.current.outerHTML,
                "Age Distribution",
                chartRef.current.offsetWidth + offSetX,
                chartRef.current.offsetHeight + offSetY,
            );
        }
    }, []);

    const handleToggleLegendItem = (legendItem: { id: string }) => {
        toggleItem(legendItem.id);
    };

    // Process chart data based on visible legend items
    const chartData = useMemo(() => {
        if (!data || legendItems.length === 0) {
            return null;
        }

        const visibleCategoryIds = legendItems.filter((item) => item.visible).map((item) => item.id);
        const filteredData = Object.fromEntries(
            Object.entries(data).filter(([categoryId]) => visibleCategoryIds.includes(categoryId)),
        );
        const filteredCategories = categories.filter(({ category }) => visibleCategoryIds.includes(category.id));

        return processAgeDistributionComparisonData(filteredData, filteredCategories);
    }, [data, categories, legendItems]);

    return (
        <Box ref={chartRef}>
            <WidgetContainer
                withBorder
                minWidth="100%"
                loaderWidth="100%"
                isLoading={isLoading}
                loaderHeight={280}
                noData={isError ? !isLoading : null}
                title={t("category.demographics.age.chart.title")}
                subtitleFilters={subtitleFilters}
                onDownloadClick={handleExportChartPng}
                downloadProps={{ PNG: true }}
                automationId="industry-analysis-demographics-comparison-age-distribution-graph"
            >
                <Stack
                    gap="24px"
                    padding="24px"
                >
                    {data ? (
                        <>
                            <AgeDistributionComparisonChartLegends
                                legendItems={legendItems}
                                onToggleLegendItem={handleToggleLegendItem}
                            />
                            <ColumnChartGraph
                                keepSameLegendsOnDataUpdate={false}
                                shouldPresentLegend={false}
                                data={chartData}
                                graphConfig={{
                                    chart: {
                                        height: 280,
                                    },
                                    xAxis: {
                                        categories: X_AXIS_AGE,
                                        labels: {
                                            style: {
                                                color: colorsPalettes.carbon[400],
                                            },
                                        },
                                        lineColor: colorsPalettes.carbon[50],
                                    },
                                    yAxis: {
                                        minRange: 50,
                                        floor: 0,
                                        gridLineColor: colorsPalettes.carbon[50],
                                    },
                                    tooltip: {
                                        enabled: false,
                                    },
                                    plotOptions: {
                                        column: {
                                            grouping: true,
                                            stacking: null,
                                            pointWidth: 40,
                                        },
                                        series: {
                                            enableMouseTracking: false,
                                            states: {
                                                hover: {
                                                    enabled: false,
                                                },
                                            },
                                            dataLabels: {
                                                enabled: true,
                                                useHTML: true,
                                                formatter: function () {
                                                    return renderToStaticMarkup(
                                                        <Text
                                                            fontVariant="dataTable2"
                                                            color={colorsPalettes.carbon[250]}
                                                            data-automation="age-distribution-bar-chart-value"
                                                        >
                                                            {valueFormatter(this.y)}
                                                        </Text>,
                                                    );
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </>
                    ) : null}
                </Stack>
            </WidgetContainer>
        </Box>
    );
};
